import React, { useEffect } from 'react';
import { Plus, X, DollarSign, Hash } from 'lucide-react';
import { ProductOption, generateVariantCombinations } from '../lib/productGroups';

export interface VariantRow {
  key: string;
  optionValues: Record<string, string>;
  sku: string;
  price: string;
  cost: string;
  barcode: string;
  quantity: string;
  enabled: boolean;
  imageIndex: number | null;
}

interface VariantsEditorProps {
  options: ProductOption[];
  setOptions: React.Dispatch<React.SetStateAction<ProductOption[]>>;
  variants: VariantRow[];
  setVariants: React.Dispatch<React.SetStateAction<VariantRow[]>>;
  defaultPrice?: string;
  defaultCost?: string;
  /** Product media gallery previews; variants can be assigned one of these images. */
  gallery?: { preview: string }[];
}

const MAX_OPTIONS = 3;

function makeKey(combo: Record<string, string>): string {
  return Object.entries(combo)
    .map(([k, v]) => `${k}=${v}`)
    .join('||');
}

export default function VariantsEditor({
  options,
  setOptions,
  variants,
  setVariants,
  defaultPrice = '',
  defaultCost = '',
  gallery = [],
}: VariantsEditorProps) {
  const [valueDrafts, setValueDrafts] = React.useState<Record<number, string>>({});

  // Regenerate variant rows whenever options change, preserving existing edits by key
  useEffect(() => {
    const combos = generateVariantCombinations(options);
    setVariants(prev => {
      const prevByKey = new Map(prev.map(v => [v.key, v]));
      return combos.map(combo => {
        const key = makeKey(combo);
        const existing = prevByKey.get(key);
        if (existing) return existing;
        return {
          key,
          optionValues: combo,
          sku: '',
          price: defaultPrice,
          cost: defaultCost,
          barcode: '',
          quantity: '0',
          enabled: true,
          imageIndex: null,
        };
      });
    });
  }, [options, defaultPrice, defaultCost, setVariants]);

  const addOption = () => {
    setOptions(prev => (prev.length < MAX_OPTIONS ? [...prev, { name: '', values: [] }] : prev));
  };

  const removeOption = (index: number) => {
    setOptions(prev => prev.filter((_, i) => i !== index));
  };

  const setOptionName = (index: number, name: string) => {
    setOptions(prev => prev.map((o, i) => (i === index ? { ...o, name } : o)));
  };

  const addValue = (index: number) => {
    const draft = (valueDrafts[index] || '').trim();
    if (!draft) return;
    setOptions(prev =>
      prev.map((o, i) =>
        i === index && !o.values.includes(draft) ? { ...o, values: [...o.values, draft] } : o
      )
    );
    setValueDrafts(prev => ({ ...prev, [index]: '' }));
  };

  const removeValue = (index: number, valueIndex: number) => {
    setOptions(prev =>
      prev.map((o, i) => (i === index ? { ...o, values: o.values.filter((_, vi) => vi !== valueIndex) } : o))
    );
  };

  const updateVariant = (key: string, field: keyof VariantRow, value: string) => {
    setVariants(prev => prev.map(v => (v.key === key ? { ...v, [field]: value } : v)));
  };

  const toggleVariant = (key: string) => {
    setVariants(prev => prev.map(v => (v.key === key ? { ...v, enabled: !v.enabled } : v)));
  };

  const setVariantImage = (key: string, index: number) => {
    setVariants(prev =>
      prev.map(v => (v.key === key ? { ...v, imageIndex: v.imageIndex === index ? null : index } : v))
    );
  };

  const enabledCount = variants.filter(v => v.enabled).length;

  return (
    <div className="space-y-5">
      {/* Options builder */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Options</h3>

        <div className="space-y-3">
          {options.map((option, index) => (
            <div key={index} className="border border-gray-200/80 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="text"
                  value={option.name}
                  onChange={e => setOptionName(index, e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                  style={{ fontSize: '16px' }}
                  placeholder="Option name (e.g. Size, Color)"
                />
                <button
                  type="button"
                  onClick={() => removeOption(index)}
                  className="h-9 w-9 flex items-center justify-center text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-100 active:scale-95 transition-all duration-150 touch-manipulation flex-shrink-0"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                  aria-label="Remove option"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Value chips */}
              {option.values.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {option.values.map((value, valueIndex) => (
                    <span
                      key={valueIndex}
                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 text-sm font-medium rounded-lg border border-blue-200/60"
                    >
                      {value}
                      <button
                        type="button"
                        onClick={() => removeValue(index, valueIndex)}
                        className="text-blue-400 hover:text-red-600"
                        aria-label={`Remove ${value}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Add value */}
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={valueDrafts[index] || ''}
                  onChange={e => setValueDrafts(prev => ({ ...prev, [index]: e.target.value }))}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addValue(index);
                    }
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                  style={{ fontSize: '16px' }}
                  placeholder="Add a value, then press Enter"
                />
                <button
                  type="button"
                  onClick={() => addValue(index)}
                  className="px-3 py-2 text-sm font-semibold text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 active:bg-blue-200 transition-all duration-150 active:scale-[0.98] touch-manipulation flex-shrink-0"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  Add
                </button>
              </div>
            </div>
          ))}
        </div>

        {options.length < MAX_OPTIONS && (
          <button
            type="button"
            onClick={addOption}
            className="mt-3 w-full flex items-center justify-center px-4 py-2.5 border-2 border-dashed border-gray-300 rounded-xl text-sm font-semibold text-blue-600 hover:border-blue-400 hover:bg-blue-50/40 active:scale-[0.99] transition-all duration-150 touch-manipulation"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add {options.length === 0 ? 'an option (e.g. Size)' : 'another option'}
          </button>
        )}
      </div>

      {/* Generated variants */}
      {variants.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900">Variants</h3>
            <span className="text-xs text-gray-500">{enabledCount} of {variants.length} selected</span>
          </div>

          <div className="space-y-3">
            {variants.map(variant => (
              <div
                key={variant.key}
                className={`border rounded-xl p-3 transition-colors ${
                  variant.enabled ? 'border-gray-200 bg-white' : 'border-gray-200/60 bg-gray-50 opacity-70'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    checked={variant.enabled}
                    onChange={() => toggleVariant(variant.key)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-semibold text-gray-900">
                    {Object.values(variant.optionValues).join(' / ')}
                  </span>
                </div>

                {variant.enabled && (
                  <div className="grid grid-cols-2 gap-2">
                    {/* Price */}
                    <div className="relative">
                      <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={variant.price}
                        onChange={e => updateVariant(variant.key, 'price', e.target.value)}
                        className="w-full pl-8 pr-2 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                        placeholder="Price"
                      />
                    </div>

                    {/* Quantity */}
                    <input
                      type="number"
                      min="0"
                      value={variant.quantity}
                      onChange={e => updateVariant(variant.key, 'quantity', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                      style={{ fontSize: '16px' }}
                      placeholder="Qty"
                    />

                    {/* Cost */}
                    <div className="relative">
                      <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={variant.cost}
                        onChange={e => updateVariant(variant.key, 'cost', e.target.value)}
                        className="w-full pl-8 pr-2 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                        placeholder="Cost"
                      />
                    </div>

                    {/* SKU */}
                    <div className="relative">
                      <Hash className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                      <input
                        type="text"
                        value={variant.sku}
                        onChange={e => updateVariant(variant.key, 'sku', e.target.value)}
                        className="w-full pl-8 pr-2 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                        placeholder="SKU"
                      />
                    </div>

                    {/* Barcode (full width) */}
                    <div className="relative col-span-2">
                      <Hash className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                      <input
                        type="text"
                        value={variant.barcode}
                        onChange={e => updateVariant(variant.key, 'barcode', e.target.value)}
                        className="w-full pl-8 pr-2 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                        placeholder="Barcode (optional)"
                      />
                    </div>

                    {/* Variant image picker (from product media gallery) */}
                    <div className="col-span-2">
                      <p className="text-xs font-medium text-gray-500 mb-1.5">Variant image</p>
                      {gallery.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {gallery.map((g, gi) => (
                            <button
                              key={gi}
                              type="button"
                              onClick={() => setVariantImage(variant.key, gi)}
                              className={`h-10 w-10 rounded-lg overflow-hidden border-2 transition-all duration-150 ${
                                variant.imageIndex === gi
                                  ? 'border-blue-500 ring-2 ring-blue-200'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                              aria-label={`Use image ${gi + 1} for this variant`}
                            >
                              <img src={g.preview} alt="" className="h-full w-full object-cover" />
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400">Add images in Media above to assign one to this variant.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
